import { auth, db } from '../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';

// Read endpoint from environment
const QUIZ_API = process.env.REACT_APP_QUIZ_API;

// Track in-flight quiz requests per contentId to prevent duplicate calls
const inFlightQuizRequests = new Map();
export const isQuizRequestInFlight = (contentId) => inFlightQuizRequests.has(contentId);

/**
 * Deprecated: Client-side quiz generation is disabled. Use generateQuizForSession with contentId.
 * If options.contentId is provided, this will delegate to the backend generator.
 */
export async function generateQuizFromContent(content, options = {}) {
  const contentId = options?.contentId;
  if (contentId) {
    return generateQuizForSession(contentId, options);
  }
  throw new Error("Client-side quiz generation is disabled. Provide contentId and use generateQuizForSession.");
}

/**
 * Generate quiz for a session by fetching session context then calling backend
 */
export const generateQuizForSession = async (contentId, options = {}) => {
  if (!contentId) throw new Error("Missing contentId for quiz generation");

  if (inFlightQuizRequests.has(contentId)) {
    return inFlightQuizRequests.get(contentId);
  }

  const promise = (async () => {
    const { maxQuestions = 25 } = options;

    try {
      if (!QUIZ_API) {
        throw new Error('Quiz API endpoint not configured. Set REACT_APP_QUIZ_API in .env');
      }
      const user = auth.currentUser;
      let token = null;
      if (user) {
        token = await user.getIdToken();
      }

      const payload = { contentId, maxQuestions };

      const resp = await fetch(QUIZ_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Quiz API error: ${resp.status} ${txt}`);
      }

      const data = await resp.json();
      if (!data || data.success === false) {
        throw new Error(`Quiz API failed: ${JSON.stringify(data)}`);
      }

      const questions = Array.isArray(data.questions) ? data.questions : [];
      return questions;
    } catch (err) {
      console.error("[quizService] generateQuizForSession error:", err);
      throw err;
    } finally {
      inFlightQuizRequests.delete(contentId);
    }
  })();

  inFlightQuizRequests.set(contentId, promise);
  return promise;
};

// Fetch the latest quiz set for a session (by timestamp). Returns { id, cards, timestamp } or null
export async function fetchLatestQuiz(contentId) {
  try {
    const user = auth.currentUser;
    if (!user || !contentId) return null;
    const quizCol = collection(db, 'users', user.uid, 'Sessions', contentId, 'Quiz');
    const q = query(quizCol, orderBy('timestamp', 'desc'));
    const snap = await getDocs(q);
    if (!snap || snap.empty) return null;
    const latest = snap.docs[0];
    const data = latest.data() || {};
    return { id: latest.id, cards: Array.isArray(data.cards) ? data.cards : [], timestamp: data.timestamp ?? null };
  } catch (e) {
    console.error('[quizService] fetchLatestQuiz error:', e);
    return null;
  }
}
